import React, { useEffect, useState } from 'react';

import { useLocation } from 'react-router-dom';

import AddIcon from '@mui/icons-material/Add';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';

import GridPagination from '~/components/Common/Elements/GridPagination';
import { useConfirmDialog } from '~/components/Common/Providers/ConfirmDialogProvider';
import AttributionCell from '~/components/GHADiscounting/AttributionCell';
import GHABulkOperationDialog from '~/components/GHAPropertyManagement/GHABulkOperationDialog';

import {
  useBulkUpsertGHAEmailDomainRules,
  useDeleteGHAEmailDomainRule,
  useGHAEmailDomainRules,
  useUpsertGHAEmailDomainRule,
} from '~/hooks/apis/connection-google/useGHAEmailDomainRules';
import useAppNavigation from '~/hooks/useAppNavigation';
import useDebounced from '~/hooks/useDebounced';

import type { GHAEmailClassification, GHAEmailDomainRule } from '~/services/ConnectionGoogleService';

import EmailDomainRuleDialog from './EmailDomainRuleDialog';
import {
  GHA_EMAIL_CLASSIFICATION_CHIP_COLORS,
  GHA_EMAIL_CLASSIFICATION_LABELS,
  GHA_EMAIL_CLASSIFICATION_OPTIONS,
  GHA_EMAIL_DOMAIN_RULES_PAGE_SIZE_OPTIONS,
} from './constants';
import { parseFilterParams, parsePaginationParams } from './utils';

const getColumns = (
  onEdit: (rule: GHAEmailDomainRule) => void,
  onDelete: (rule: GHAEmailDomainRule) => void,
  isDeleting: boolean,
): Array<GridColDef<GHAEmailDomainRule>> => [
  { field: 'domain', headerName: 'Domain', flex: 1, minWidth: 220 },
  {
    field: 'classification',
    headerName: 'Classification',
    width: 150,
    renderCell: ({ row }) => (
      <Chip
        size="small"
        variant="outlined"
        color={GHA_EMAIL_CLASSIFICATION_CHIP_COLORS[row.classification]}
        label={GHA_EMAIL_CLASSIFICATION_LABELS[row.classification]}
      />
    ),
  },
  {
    field: 'reason',
    headerName: 'Reason',
    flex: 1,
    minWidth: 240,
    renderCell: ({ row }) => (
      <Typography variant="body2" color="text.secondary">
        {row.reason ?? '—'}
      </Typography>
    ),
  },
  {
    field: 'updatedAt',
    headerName: 'Created / updated',
    width: 260,
    renderCell: ({ row }) => (
      <AttributionCell
        createdBy={row.createdBy}
        updatedBy={row.updatedBy}
        createdAt={row.createdAt}
        updatedAt={row.updatedAt}
      />
    ),
  },
  {
    field: 'actions',
    headerName: 'Actions',
    width: 170,
    sortable: false,
    align: 'right',
    headerAlign: 'right',
    renderCell: ({ row }) => (
      <>
        <Button size="small" onClick={() => onEdit(row)}>
          Edit
        </Button>
        <Button size="small" color="error" disabled={isDeleting} onClick={() => onDelete(row)}>
          Delete
        </Button>
      </>
    ),
  },
];

export default function EmailDomainRulesPanel() {
  const location = useLocation();
  const { push, replace } = useAppNavigation();
  const searchParams = new URLSearchParams(location.search);
  const paginationModel = parsePaginationParams(searchParams);
  // Filters live in the URL with the page, as on GHA Property Management, so a refresh or a shared
  // link keeps the whole view.
  const filters = parseFilterParams(searchParams);

  const [search, setSearch] = useState(filters.domain);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [ruleToEdit, setRuleToEdit] = useState<GHAEmailDomainRule | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const showConfirmDialog = useConfirmDialog();
  const debouncedSearch = useDebounced(search.trim(), 300);

  // A new search starts again from the first page. Replaced rather than pushed, so typing does not
  // leave a history entry per pause.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if ((params.get('domain') ?? '') === debouncedSearch) {
      return;
    }
    if (debouncedSearch === '') {
      params.delete('domain');
    } else {
      params.set('domain', debouncedSearch);
    }
    params.set('page', '0');
    replace({ search: params.toString() });
  }, [debouncedSearch, location.search, replace]);

  const { data, isFetching, error } = useGHAEmailDomainRules({
    classification: filters.classification === '' ? undefined : filters.classification,
    domain: filters.domain === '' ? undefined : filters.domain,
    page: paginationModel.page + 1,
    limit: paginationModel.pageSize,
  });
  const upsertMutation = useUpsertGHAEmailDomainRule();
  const deleteMutation = useDeleteGHAEmailDomainRule();
  const bulkMutation = useBulkUpsertGHAEmailDomainRules();

  const handlePaginationModelChange = (model: GridPaginationModel) => {
    const params = new URLSearchParams(location.search);
    params.set('page', String(model.page));
    params.set('pageSize', String(model.pageSize));
    push({ search: params.toString() });
  };

  const handleClassificationChange = (classification: '' | GHAEmailClassification) => {
    const params = new URLSearchParams(location.search);
    if (classification === '') {
      params.delete('classification');
    } else {
      params.set('classification', classification);
    }
    params.set('page', '0');
    push({ search: params.toString() });
  };

  const handleEdit = (rule: GHAEmailDomainRule) => {
    setRuleToEdit(rule);
    setDialogOpen(true);
  };

  const handleDelete = async (rule: GHAEmailDomainRule) => {
    const confirmed = await showConfirmDialog({
      title: `Delete ${rule.domain}?`,
      description: 'Addresses at this domain fall back to the property-name and keyword checks, then unknown.',
    });
    if (confirmed) {
      deleteMutation.mutate(rule.domain);
    }
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setRuleToEdit(null);
    setSubmitError(null);
  };

  if (error) {
    return <Alert severity="error">Failed to load email domain rules</Alert>;
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="email-domain-rules-classification-filter">Classification</InputLabel>
            <Select
              labelId="email-domain-rules-classification-filter"
              label="Classification"
              value={filters.classification}
              onChange={(event) => handleClassificationChange(event.target.value)}
            >
              <MenuItem value="">All classifications</MenuItem>
              {GHA_EMAIL_CLASSIFICATION_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            size="small"
            label="Search domain"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button variant="text" startIcon={<CloudUploadIcon />} onClick={() => setBulkOpen(true)}>
            Bulk upload
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
            New rule
          </Button>
        </Stack>
      </Stack>

      <Alert severity="info" sx={{ mb: 2 }}>
        Each rule classifies every GHA shopper email at that domain. Shadow mode: the result is logged on voucher mint
        and nothing acts on it yet. Large webmail domains (gmail.com, outlook.com, …) can only be Consumer.
      </Alert>

      <DataGrid
        rows={data?.result ?? []}
        columns={getColumns(handleEdit, handleDelete, deleteMutation.isPending)}
        getRowId={(row) => row.domain}
        getRowHeight={() => 'auto'}
        pageSizeOptions={GHA_EMAIL_DOMAIN_RULES_PAGE_SIZE_OPTIONS}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationModelChange}
        paginationMode="server"
        rowCount={data?.total ?? 0}
        loading={isFetching}
        disableRowSelectionOnClick
        disableColumnMenu
        slots={{ pagination: GridPagination }}
        sx={{ '& .MuiDataGrid-cell': { py: 1, display: 'flex', alignItems: 'center' } }}
        autoHeight
      />

      <EmailDomainRuleDialog
        open={dialogOpen}
        initialRule={ruleToEdit}
        isSaving={upsertMutation.isPending}
        submitError={submitError}
        onClose={closeDialog}
        onSubmit={async (domain, input) => {
          setSubmitError(null);
          try {
            await upsertMutation.mutateAsync({ domain, input });
            closeDialog();
          } catch (submitException) {
            // Protected-domain and format 400s carry the reason — show verbatim.
            setSubmitError(submitException instanceof Error ? submitException.message : 'Failed to save the rule');
          }
        }}
      />

      <GHABulkOperationDialog
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        title="Bulk upload email domain rules"
        csvHeader="domain,classification,reason"
        csvRows={['hyatt.com,hotel,Hotel chain domain', 'gmail.com,consumer,Webmail', 'example.com,uncertain,']}
        csvNotes={
          <>
            • Header row must be <strong>domain,classification,reason</strong>
            <br />• Classification is consumer, hotel or uncertain; reason may be empty
            <br />• Each row creates or overwrites that domain. Any bad row rejects the whole file.
          </>
        }
        submitButtonLabel="Upload"
        successMessage="Email domain rules uploaded."
        // The mutation's snackbar reports the created/updated counts, and the list refetches, so
        // the dialog's own "refresh the page" success copy is not needed.
        showSuccessAlert={false}
        showSuccessSnackbar={false}
        onSubmit={async (file) => {
          await bulkMutation.mutateAsync(file);
        }}
      />
    </Box>
  );
}
